import { decodeJwtClaims, jwtExpiry } from '../lib/jwt'
import { Role } from '../types/prisma-enums'

/**
 * §10.0 — the staff session, and the credential every adult dashboard holds.
 *
 * 🔴 IT PERSISTS. THIS FILE USED TO SAY THE OPPOSITE IN CAPITALS, AND THE
 * REVERSAL IS DELIBERATE RATHER THAN AN EROSION.
 *
 * What it said: *"IN MEMORY. NOT `localStorage`. THIS IS THE ONE DECISION IN THE
 * FILE THAT IS NOT NEGOTIABLE"* — because a token that outlives the tab means
 * *"the next class would open the previous teacher's dashboard"*. That cost is
 * real and is restated below rather than dropped. Three things moved under it:
 *
 *   1. **`POST /auth/refresh` EXISTS AND IS WIRED.** When the rule was written
 *      nothing rotated, so persisting bought a session that died 15 minutes
 *      later anyway — all of the cost and none of the benefit. `refresh.ts` now
 *      rotates on every 401 with a single-flight lock, and the server re-reads
 *      `user.isActive` on each rotation (`staff-auth.service.ts` §refresh), so a
 *      deactivated teacher loses a persisted session within one access-token
 *      lifetime rather than within the refresh token's thirty days.
 *   2. **`architecture.md` §21 ALREADY SPECIFIES THIS MODEL** — *"access in
 *      memory, refresh + device in `localStorage`, one transparent refresh-retry
 *      on a 401"*. Memory-only staff tokens were the divergence, not this.
 *   3. **THE PRODUCT DECISION WAS MADE EXPLICITLY.** A staff session ends when
 *      somebody presses Sign out, and at no other moment: not on a refresh, not
 *      on the back button, not on closing the tab. Losing a dashboard to a
 *      mis-swiped back gesture reads as a bug to the person it happens to, and
 *      they cannot tell it apart from one.
 *
 * ⚠️ WHAT IT COSTS, SAID PLAINLY AND NOT SOFTENED: on a SHARED CLASSROOM LAPTOP
 * the next person to open the browser gets the previous teacher's dashboard.
 * Sign out is now the only thing that ends a session, so it is the only thing
 * that protects one. 📋 An idle expiry was offered and declined; if that trade
 * is revisited, `hydrate()` below is the single place it belongs — it is the one
 * function that decides whether a stored pair is still a session.
 *
 * ✅ P0-13 / D9 ANSWERS THAT COST WITHOUT UNDOING THE ABOVE: "Keep me signed
 * in", unticked by default on the teacher and school doors, keeps the pair in
 * `sessionStorage` (this tab only, gone when it closes) and the server caps that
 * session at 12 hours. Ticked, it is exactly the persistence argued for here.
 * See `tierFor`.
 *
 * 🔴 AN IMPERSONATION GRANT IS NEVER WRITTEN TO STORAGE, and that exception
 * survives all of the above. See `beginImpersonation`.
 *
 * ⚠️ SO IT NOW MATCHES `parentSession.ts` RATHER THAN DIFFERING FROM IT, which
 * is a simplification the two stores had been paying for: `refresh.ts` had to
 * describe them as *"differing in lifetime rather than in protocol"* on every
 * path that touched both.
 *
 * 🔴 A MODULE, NOT A REACT CONTEXT — `device.ts` and `parentSession.ts`'s shape
 * and reason: the token is read by plain `fetch` helpers in `shared/api/`, which
 * are not inside React's lifecycle.
 */

/**
 * One key, one value, both halves together — see `read`.
 *
 * ⚠️ EXPORTED SO THE `storage` EVENT CAN BE RECOGNISED. `AppRouter` listens for
 * another tab writing this key; comparing against a string literal copied into
 * the router is how the two drift the day the key is renamed.
 */
export const STAFF_SESSION_KEY = 'kalqy.staff'

/** Exactly what `POST /auth/login` and `POST /auth/refresh` return. */
export interface StaffTokens {
  accessToken: string
  refreshToken: string
}

/**
 * The signed-in principal, as far as the token's own claim describes them.
 *
 * 🔴 `role` IS READ FROM THE TOKEN AND IS NAVIGATION, NOT AUTHORISATION. It
 * decides which sidebar to draw. Every screen behind it calls an endpoint that
 * scopes itself server-side (§8.1: *"a guard cannot scope a collection"*), and a
 * forged claim buys the forger a menu and a row of 403s. `routes.ts` states the
 * same rule about `mayNavigate`; this is where the input to it comes from.
 */
export interface StaffSession {
  role: Role
  accessToken: string
  /** Staff only — absent for a parent, who has children rather than a school. */
  schoolId: string | null
  /**
   * X4 — set when this token was minted by `ImpersonationService`, carrying the
   * operator's id. Present here so the banner `admin-panel.md` §8 requires
   * (*"never a silent view-as"*) can be rendered from the session rather than
   * from a flag some screen remembers to pass down.
   */
  impersonatorId: string | null
  /** Unverified `exp`, for the "your session expired" sentence. Never a gate. */
  expiresAt: Date | null
}

/**
 * Minimal `localStorage` shape, injectable so the tests (and Node) do not need
 * a DOM, and so a browser that throws on access cannot take the boot path down.
 * The same shim as `parentSession.ts` and `kidSession.ts`, deliberately — two
 * storage shims with different semantics is how one of them ends up untested.
 */
export interface SessionStorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function defaultStorage(): SessionStorageLike | null {
  try {
    // Absent under Node (where the tests run), and Safari private mode throws
    // on ACCESS rather than on use — so this is a try/catch, not a truthiness
    // check.
    return (globalThis as { localStorage?: SessionStorageLike }).localStorage ?? null
  } catch {
    return null
  }
}

/** The per-tab store, under the same guard and for the same reasons. */
function defaultTabStorage(): SessionStorageLike | null {
  try {
    return (globalThis as { sessionStorage?: SessionStorageLike }).sessionStorage ?? null
  } catch {
    return null
  }
}

/**
 * P0-13 / D9 — "Keep me signed in", and where each answer puts the pair.
 *
 * 🔴 THE SHARED CLASSROOM LAPTOP IS WHY THERE ARE TWO STORES. Everything above
 * argues for persistence and states its cost plainly — *"the next person to open
 * the browser gets the previous teacher's dashboard"*. The checkbox is the
 * answer that cost was waiting for: unchecked (the default on the teacher and
 * school doors), the pair lives in `sessionStorage`, which survives a reload and
 * a mis-swiped back gesture but dies with the tab; and the server gives that
 * session a 12-hour refresh that rotation never extends. Checked, it is the
 * thirty-day `localStorage` session this file has always kept.
 *
 * ⚠️ `'tab'` WITH NO USABLE `sessionStorage` IS MEMORY-ONLY, NEVER `localStorage`.
 * Falling back to the persistent store would quietly turn "do not keep me signed
 * in" into the opposite on exactly the browsers that refuse storage. Losing the
 * session on a reload is the honest degradation of a choice to keep nothing.
 */
export type StaffSessionTier = 'local' | 'tab'

/**
 * Which store a sign-in writes to. Pure, so the rule is testable without a DOM:
 * `remember` undefined means "no choice was offered" (the operator door, an
 * older caller) and keeps the persistent store, which is what the server's own
 * default (`remember` absent → 30 days, operators capped at 12 h) matches.
 */
export function tierFor(remember: boolean | undefined): StaffSessionTier {
  return remember === false ? 'tab' : 'local'
}

let storage: SessionStorageLike | null = defaultStorage()
let tabStorage: SessionStorageLike | null = defaultTabStorage()

/**
 * Where the CURRENT pair lives. Set by a sign-in that states a choice, and by
 * `sync` when it adopts a pair from one store or the other — so a rotation
 * (`refresh.ts` calls `setStaffSession(tokens)` with no options) writes the
 * replacement back to the same store the spent token came from.
 */
let tier: StaffSessionTier = 'local'

/** `persistent` below, for the per-tab store. Cleared the first time it throws. */
let tabPersistent = true

/*
 * 🔴 STORAGE IS THE TRUTH AND THIS IS THE FALLBACK, WHICH IS THE ORDER
 * `parentSession.ts` HAS ALWAYS USED AND THE ORDER TWO TABS REQUIRE. See `sync`.
 */
let session: StaffSession | null = null
let refreshToken: string | null = null

/**
 * Cleared for good the first time a storage call throws.
 *
 * 🔴 WITHOUT IT, A BROWSER THAT REFUSES STORAGE COULD NOT SIGN IN AT ALL. `sync`
 * treats "storage holds no pair" as a sign-out — correct when the write
 * succeeded and another tab removed it, catastrophic when the write itself
 * silently failed: `setStaffSession` would fill memory, the very next read would
 * find nothing on disk and wipe it, and the sign-in would appear to do nothing.
 * Safari private mode throws on ACCESS, so this is discovered on first use
 * rather than declared up front — and from then on the tab is memory-only,
 * which is exactly what this file did for everybody before it persisted.
 */
let persistent = true

/**
 * Test seam, and the escape hatch if a browser's storage throws.
 *
 * ⚠️ IT RESETS THE HYDRATED FLAG AND THE CACHE. A test that swapped the storage
 * and still got the previous test's session would be asserting against memory it
 * thought it had replaced — which is exactly the bug this seam exists to make
 * visible.
 */
export function setStaffSessionStorage(
  next: SessionStorageLike | null,
  /**
   * The per-tab store. Omitted means "this environment has none" — which is
   * exactly what Node gives the existing tests, so every test written before the
   * checkbox existed keeps asserting against the one store it always had.
   */
  nextTab: SessionStorageLike | null = null,
): void {
  storage = next
  tabStorage = nextTab
  session = null
  refreshToken = null
  operatorSession = null
  persistent = true
  tabPersistent = true
  tier = 'local'
}

/**
 * Every valid `Role`, as a set, so an unknown string in a claim is refused
 * rather than cast.
 *
 * 🔴 FAIL CLOSED. `homeFor` already sends an unrecognised role to the landing
 * page instead of guessing a tier, and this is the same decision one layer
 * earlier: a token claiming `role: 'OWNER'` produces **no session at all**,
 * because a session with a role nothing recognises is a signed-in user with an
 * empty menu and no way to understand why.
 */
const ROLES = new Set<string>(Object.values(Role))

/**
 * Build a session from a token pair, or `null` if the token says nothing we
 * recognise.
 *
 * ⚠️ PURE AND EXPORTED SO IT CAN BE TESTED WITHOUT MODULE STATE — the repo has
 * no DOM renderer (OD-60), so a decision made only inside a setter is a decision
 * no test can reach.
 */
export function sessionFromTokens(tokens: StaffTokens): StaffSession | null {
  const claims = decodeJwtClaims(tokens.accessToken)
  if (!claims) return null

  const { role, schoolId, imp } = claims
  if (typeof role !== 'string' || !ROLES.has(role)) return null

  return {
    role: role as Role,
    accessToken: tokens.accessToken,
    /*
     * A non-string `schoolId` is dropped rather than coerced. It is an
     * authorisation input server-side (§8.1: *"an admin who can name their own
     * school can name any school"*), so the only honest client-side handling of
     * a shape we did not expect is to hold nothing.
     */
    schoolId: typeof schoolId === 'string' ? schoolId : null,
    impersonatorId: typeof imp === 'string' ? imp : null,
    expiresAt: jwtExpiry(tokens.accessToken),
  }
}

/**
 * What is on disk, or `null`.
 *
 * 🔴 BOTH OR NEITHER, `parentSession.ts`'s rule and for its reason: a half-cache
 * is a staff member the app believes is signed in and cannot re-authenticate —
 * every read 401s, the rotation has no token to present, and the only escape is
 * clearing site data.
 */
function storeOf(which: StaffSessionTier): SessionStorageLike | null {
  return which === 'tab' ? tabStorage : storage
}

/** Whether `which` can be trusted to answer — present, and has not thrown. */
function usable(which: StaffSessionTier): boolean {
  return which === 'tab' ? tabStorage !== null && tabPersistent : storage !== null && persistent
}

function markBroken(which: StaffSessionTier): void {
  if (which === 'tab') tabPersistent = false
  else persistent = false
}

function read(which: StaffSessionTier): StaffTokens | null {
  let raw: string | null
  try {
    raw = storeOf(which)?.getItem(STAFF_SESSION_KEY) ?? null
  } catch {
    // Not "nothing is stored" — "this browser will not tell us". The difference
    // decides whether `sync` may treat the answer as a sign-out.
    markBroken(which)
    return null
  }
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { accessToken, refreshToken: stored } = parsed as Partial<StaffTokens>
    if (typeof accessToken !== 'string' || accessToken.length === 0) return null
    if (typeof stored !== 'string' || stored.length === 0) return null
    return { accessToken, refreshToken: stored }
  } catch {
    return null
  }
}

function write(which: StaffSessionTier, tokens: StaffTokens | null): void {
  try {
    if (tokens === null) storeOf(which)?.removeItem(STAFF_SESSION_KEY)
    // Written as ONE value: a half-cache is worse than no cache (see `read`).
    else storeOf(which)?.setItem(STAFF_SESSION_KEY, JSON.stringify(tokens))
  } catch {
    /*
     * Non-fatal, and it drops this store to memory-only. A browser that cannot
     * persist still works — the session is in module state either way, and the
     * only thing lost is surviving a reload. Throwing here would turn a storage
     * quirk into a sign-in that cannot complete; leaving the store marked usable
     * would be worse still, because `sync` would then read the pair back as
     * absent and treat the sign-in as a sign-out.
     */
    markBroken(which)
  }
}

/**
 * Bring module state level with storage, and return the session.
 *
 * 🔴 EVERY READ GOES THROUGH THIS, NOT THROUGH A ONE-TIME HYDRATION, AND TWO
 * TABS ARE WHY. A refresh token is single-use: `staff-auth.service.ts` claims
 * the presented `jti` with a `deleteMany` and refuses when the count is zero.
 * Two tabs of one dashboard therefore share ONE rotatable credential. If each
 * cached its own copy in memory, the first tab to rotate would invalidate the
 * token the second is still holding — and fifteen minutes later that second tab
 * would present a dead `jti`, be refused, and sign the user out of BOTH, since
 * its sign-out clears the storage the first tab was living on. Re-reading is
 * what makes tab two rotate with tab one's replacement instead.
 *
 * ⚠️ `parentSession.ts` HAS ALWAYS DONE THIS — every one of its getters calls
 * `read()`. The staff store is the one that was memory-first, and it could
 * afford to be while a staff session died with its tab, because two tabs then
 * meant two separate sign-ins and two separate credentials. Persisting removed
 * that, so this is not an addition to the change; it is part of it.
 *
 * 🔴 STORAGE HOLDING NOTHING IS A SIGN-OUT, AND THAT IS DELIBERATE. Another tab
 * pressed Sign out — the credential is revoked server-side — so this tab must
 * not carry on with a copy. `persistent` is what keeps that from firing when the
 * write silently failed instead.
 *
 * 🔴 IT REFUSES TO TOUCH AN IMPERSONATION SESSION. What is on disk while X4 is
 * running is the OPERATOR'S own pair, so adopting it here would end the
 * impersonation halfway through — swapping an operator back into themselves with
 * nobody having asked, which is the mirror image of the silent view-as
 * `admin-panel.md` §8 forbids.
 *
 * ⚠️ AN **EXPIRED** ACCESS TOKEN IS ADOPTED LIKE ANY OTHER. `exp` is read
 * unverified and is never an authorisation decision here (`parentSession.ts`
 * says the same at length); the refresh token beside it is what makes the
 * session recoverable, and `client.ts` rotates on the first 401. Refusing on our
 * own clock would sign out every user whose laptop has the wrong date — and at a
 * fifteen-minute `JWT_ACCESS_TTL`, nearly everyone who closed the tab yesterday.
 */
function sync(): StaffSession | null {
  if (operatorSession !== null) return session
  /*
   * ⚠️ THE CURRENT PAIR'S OWN STORE DECIDES WHETHER "NOTHING ON DISK" MAY BE READ
   * AS A SIGN-OUT. A session kept in memory because its store refused (or, for an
   * unticked "Keep me signed in", because there is no per-tab store) must not be
   * wiped by a read of a store it was never written to.
   */
  if (session !== null && !usable(tier)) return session
  if (!usable('tab') && !usable('local')) return session

  /*
   * 🔴 THE PER-TAB STORE FIRST. It holds this tab's own unticked sign-in; the
   * shared `localStorage` can only hold a pair this browser chose to remember.
   * Reading the shared one first would let a remembered pair outrank the session
   * this tab is actually in.
   */
  const fromTab = usable('tab') ? read('tab') : null
  if (session !== null && !usable(tier)) return session
  const fromLocal = fromTab === null && usable('local') ? read('local') : null
  // `read` clears a store's flag when the browser refuses rather than answers.
  if (session !== null && !usable(tier)) return session

  const stored = fromTab ?? fromLocal
  if (!stored) {
    session = null
    refreshToken = null
    return null
  }
  const storedTier: StaffSessionTier = fromTab !== null ? 'tab' : 'local'

  // The common case by far: nothing has changed under us.
  if (session !== null && stored.accessToken === session.accessToken) {
    refreshToken = stored.refreshToken
    tier = storedTier
    return session
  }

  /*
   * A pair whose claim names a role we cannot describe is discarded, not kept —
   * `sessionFromTokens`' fail-closed rule applied to what came off the disk. A
   * refresh token held beside a principal we cannot name is the half-state
   * `read` refuses one line up.
   */
  const next = sessionFromTokens(stored)
  session = next
  refreshToken = next ? stored.refreshToken : null
  tier = storedTier
  if (!next) write(storedTier, null)
  return session
}

/** What a sign-in may say about where its pair should live. */
export interface StaffSessionOptions {
  /**
   * The "Keep me signed in" box. `false` → this tab only; `true` → this
   * browser. Absent → keep the store the current pair already lives in, which
   * is what a ROTATION means (`refresh.ts` passes no options).
   */
  remember?: boolean
}

/**
 * Called by the sign-in screen with the pair `POST /auth/login` returned, and
 * by `refresh.ts` with each rotation's replacement.
 *
 * 🔴 A SIGN-IN THAT STATES A CHOICE EMPTIES THE OTHER STORE. A teacher who ticks
 * nothing on a laptop where yesterday's teacher ticked the box must not leave
 * yesterday's thirty-day pair sitting in `localStorage` underneath them — the
 * next reload of another tab would hand it straight back. A rotation states no
 * choice and touches only the store it came from.
 */
export function setStaffSession(
  tokens: StaffTokens,
  options?: StaffSessionOptions,
): StaffSession | null {
  const next = sessionFromTokens(tokens)
  const chosen: StaffSessionTier =
    options !== undefined && 'remember' in options ? tierFor(options.remember) : tier
  /*
   * ⚠️ BOTH OR NEITHER, `parentSession.ts`'s rule. A refresh token kept beside a
   * session that failed to parse is a credential held for a principal the app
   * cannot describe.
   */
  session = next
  refreshToken = next ? tokens.refreshToken : null
  if (next) {
    tier = chosen
    write(chosen, tokens)
    if (options !== undefined) write(chosen === 'tab' ? 'local' : 'tab', null)
  } else {
    write(chosen, null)
  }
  return next
}

/** Whether the current pair is kept for this tab only (an unticked "Keep me signed in"). */
export function staffSessionTier(): StaffSessionTier {
  sync()
  return tier
}

export function getStaffSession(): StaffSession | null {
  return sync()
}

/** `bearerFor('user')` — the access token, or null. */
export function getStaffToken(): string | null {
  return sync()?.accessToken ?? null
}

/**
 * The refresh token, for `POST /auth/refresh`.
 *
 * ⚠️ HELD SEPARATELY FROM THE SESSION, so a screen destructuring a session
 * cannot accidentally put a thirty-day credential into a prop, a log line or a
 * React DevTools tree. The access token has to be in the session because every
 * API call needs it; this one has exactly one caller.
 */
export function getStaffRefreshToken(): string | null {
  // `sync` first: the token this returns must be the one currently on disk, or
  // a second tab rotates with a `jti` the first one already spent. See `sync`.
  sync()
  return refreshToken
}

/**
 * Sign out locally.
 *
 * 🔴 IT CLEARS BEFORE THE NETWORK CALL AND CANNOT FAIL. `POST /auth/logout`
 * revokes the refresh token server-side and should be called — but a sign-out
 * that only works when the wifi does is a sign-out that leaves the previous
 * teacher's dashboard open on a school laptop. The local half is unconditional;
 * `endStaffSession` in `shared/api/auth.ts` does the remote half afterwards and
 * swallows its own failure.
 *
 * 🔴 AND SINCE THE SESSION PERSISTS, THIS IS NOW THE ONLY THING THAT ENDS ONE.
 * The storage write is part of the unconditional local half for that reason: a
 * sign-out that cleared memory and left the pair on disk would put the same
 * dashboard back on the next reload.
 */
export function clearStaffSession(): void {
  session = null
  refreshToken = null
  operatorSession = null
  // Both stores: a sign-out that left the other one holding a pair would put a
  // dashboard back on the next reload.
  write('tab', null)
  write('local', null)
}

/* ──────────────────────────────────────────────────── X4 · acting as ── */

/**
 * The operator's own session, held while they are acting as somebody else.
 *
 * 🔴 IT IS STASHED RATHER THAN RE-FETCHED, SO STOPPING WORKS WITH NO NETWORK.
 * The alternative is calling `POST /auth/refresh` to get back to yourself, which
 * makes "stop acting as them" fail on a bad connection — and an operator stuck
 * inside somebody else's account is precisely the state `admin-panel.md` §8
 * exists to prevent. This adds no credential that was not already in memory: the
 * operator's access token was here a moment ago and is here again.
 */
let operatorSession: StaffSession | null = null

/**
 * Swap into an impersonation session (X4).
 *
 * 🔴 FAIL CLOSED ON A TOKEN THAT DOES NOT CARRY `imp`. A grant without the
 * operator's id in it is not an impersonation session — it is an ordinary staff
 * token — and swapping into one would put the operator inside another account
 * with **no banner**, because the banner renders from `impersonatorId`. That is
 * the silent view-as §8 forbids, arriving through the one door built to prevent
 * it. `sessionFromTokens` applies the same rule to an unrecognised role.
 *
 * 🔴 NOTHING IS WRITTEN TO STORAGE HERE, AND THAT IS THE ONE PLACE THE
 * PERSISTENCE ABOVE STOPS. What is on disk stays the OPERATOR'S OWN pair, so a
 * reload mid-impersonation comes back as the operator rather than as the person
 * they were inspecting. A persisted grant would be worse in both directions: it
 * would put somebody inside another account across a browser restart with
 * `operatorSession` gone — which is `endImpersonationLocally` returning null and
 * `stopActingAs` resolving it by signing out, i.e. an operator stranded — and it
 * would outlive the fifteen-minute clock §8.1 relies on to end it.
 *
 * ⚠️ THE REFRESH TOKEN IS DELIBERATELY LEFT ALONE AND STILL BELONGS TO THE
 * OPERATOR. `ImpersonationService` issues none — a refresh token would let the
 * session renew itself for thirty days, which is a permanent second identity —
 * so there is nothing to replace it with, and pairing the impersonated access
 * token with the operator's refresh token would let a `POST /auth/refresh`
 * silently promote the session back without anybody choosing to. `refresh.ts`
 * refuses to rotate at all while `impersonatorId` is set, for that reason.
 */
export function beginImpersonation(accessToken: string): StaffSession | null {
  sync()
  // The refresh token is irrelevant to parsing; a placeholder keeps
  // `sessionFromTokens` the single definition of "what a token says".
  const next = sessionFromTokens({ accessToken, refreshToken: '' })
  if (!next || next.impersonatorId === null) return null

  // Nothing to return to means this is not a swap — refuse rather than strand
  // the operator inside an account with no way back.
  if (session === null) return null

  operatorSession = session
  session = next
  return next
}

/**
 * Return to the operator's own session.
 *
 * ⚠️ THIS IS THE LOCAL HALF ONLY, AND IT CANNOT REVOKE THE IMPERSONATION TOKEN.
 * Access tokens are verified by signature with no store lookup (§8.1), so the
 * grant dies on its own fifteen-minute clock whatever this does.
 * `POST /auth/impersonate/end` records the intent, which is what an investigator
 * reads; this is what stops the app *using* the credential, and it happens
 * unconditionally — `signOut`'s rule, for the same reason.
 *
 * ⚠️ NO STORAGE WRITE HERE EITHER — see `beginImpersonation`. What is on disk
 * never stopped being the operator's own pair, so returning to it is a memory
 * swap and nothing more.
 */
export function endImpersonationLocally(): StaffSession | null {
  if (operatorSession === null) return null
  session = operatorSession
  operatorSession = null
  return session
}
